import { useCallback, useEffect, useMemo, useRef } from 'react';
import useDemandStudioData from './useDemandStudioData';
import { buildLifecareSectionDefinitions, SECTION_UI_STATE } from '@demandproai/shared';

// Section definitions cover EVERY lifecare client (not just the selected one) so the case-level
// editComments array round-trips all clients on save and switching client never drops a comment;
// the studio only displays the active client's slice (see sectionByTabType). The SUMMARY tab is
// excluded -- it has no comment field on the backend.
const useLifecareStudioSuggestedEdits = ({ 
  caseId, 
  isOpen, 
  selectedClientId, 
  lifecareClientIdAndDisplayNames,
  onSuggestedEditApplied
}) => {
  const customSectionDefinitions = useMemo(
    () => buildLifecareSectionDefinitions({ clientIds: lifecareClientIdAndDisplayNames.map(obj=>obj.clientId) }),
    [lifecareClientIdAndDisplayNames]
  );

  const {
    sectionComments,
    updateSectionComment,
    handleRegenerateSection: demandStudioSectionRefresh,
    getSectionUiState,
    isAnySectionRegenerating,
    isLoading: isSuggestedEditsLoading,
    suggestedEditsSettledSignal,
  } = useDemandStudioData({
    isOpen,
    caseId,
    customSectionDefinitions,
    // the lifecare studio runs its own regeneration on close, and the demand rebuild is a superset of it
    shouldRegenerateOnUnmount: false,
  });

  // Lookup by tab type, scoped to the active client: the body renders one tab at a time and must
  // grab THIS client's section in O(1) (sectionComments holds every client's rows).
  const sectionByTabType = useMemo(() => {
    const map = new Map();
    sectionComments
      .filter((section) => section.targetId === selectedClientId)
      .forEach((section) => {
        map.set(section.type, section);
      });
    return map;
  }, [sectionComments, selectedClientId]);

  // sections whose suggested edit was submitted from this studio session and is not settled yet
  const pendingSectionIdsRef = useRef(new Set());

  const handleRegenerateSection = useCallback(async (sectionId) => {
    pendingSectionIdsRef.current.add(sectionId);
    await demandStudioSectionRefresh(sectionId);
  }, [demandStudioSectionRefresh]);

  // The agent writes the plan server side, so the studio only sees it after a refetch. Reacting to
  // the section ui state (fed by both the completion socket and the status poll) means a dropped
  // socket no longer leaves the form showing the pre-edit plan until the user reloads the page.
  useEffect(() => {
    const completedSectionIds = [...pendingSectionIdsRef.current].filter(
      (sectionId) => getSectionUiState(sectionId) === SECTION_UI_STATE.SUCCESS
    );
    if (!completedSectionIds.length) return;
    completedSectionIds.forEach((sectionId) => pendingSectionIdsRef.current.delete(sectionId));
    onSuggestedEditApplied();
  }, [getSectionUiState, onSuggestedEditApplied]);

  return {
    sectionByTabType,
    sectionComments,
    updateSectionComment,
    handleRegenerateSection,
    getSectionUiState,
    isAnySectionRegenerating,
    isSuggestedEditsLoading,
    suggestedEditsSettledSignal,
  };
};

export default useLifecareStudioSuggestedEdits;
